import React, { useMemo } from 'react';
import {
  Grid,
  TextField,
} from '@mui/material';

const formInputStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    transition: 'all 0.2s ease-in-out',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#7522D1',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#7522D1',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#7522D1',
  },
};

const OperationalInformation = ({ formData, handleInputChange, errors = {}, touched = {} }) => {
  const validateField = (fieldName, value) => {
    const validators = {
      employeeCount: (val) => {
        if (val && (isNaN(val) || parseInt(val) < 1 || parseInt(val) > 1000000)) {
          return 'Employee count must be between 1 and 1,000,000';
        }
        return null;
      },
      foundedYear: (val) => {
        if (val) {
          const year = parseInt(val);
          const currentYear = new Date().getFullYear();
          if (isNaN(year) || year < 1600 || year > currentYear) {
            return `Please enter a valid year between 1600 and ${currentYear}`;
          }
        }
        return null;
      },
      businessHours: (val) => {
        if (val && val.length > 100) {
          return 'Business hours must not exceed 100 characters';
        }
        return null;
      },
      timeZone: (val) => {
        if (val && val.length > 50) {
          return 'Time zone must not exceed 50 characters';
        }
        return null;
      },
      primaryLanguage: (val) => {
        if (val && val.length > 50) {
          return 'Language must not exceed 50 characters';
        }
        return null;
      },
      currency: (val) => {
        if (val && !/^[A-Z]{3}$/.test(val)) {
          return 'Currency must be a valid 3-letter code (e.g., USD, EUR)';
        }
        return null;
      },
      revenue: (val) => {
        if (val && !/^\$?\d{1,3}(,\d{3})*(\.\d{2})?$/.test(val.replace(/\s/g, ''))) {
          return 'Please enter a valid revenue amount (e.g., 1,000,000 or $1,000,000.00)';
        }
        return null;
      }
    };

    return validators[fieldName] ? validators[fieldName](value) : null;
  };

  const fieldErrors = useMemo(() => {
    const errs = {};
    Object.keys(formData).forEach(key => {
      if (touched[key]) {
        const error = validateField(key, formData[key]);
        if (error) errs[key] = error;
      }
    });
    return errs;
  }, [formData, touched]);

  return (
    <Grid container spacing={3}>
      <Grid item size={{xs:12, md:6}}>
        <TextField
          fullWidth
          label="Employee Count"
          value={formData.employeeCount}
          onChange={(e) => handleInputChange('employeeCount', e.target.value)}
          variant="outlined"
          size="small"
          type="number"
          placeholder="100"
          inputProps={{ min: 1, max: 1000000 }}
          error={!!errors.employeeCount || !!fieldErrors.employeeCount}
          helperText={errors.employeeCount || fieldErrors.employeeCount || ' '}
          sx={formInputStyle}
        />
      </Grid>
      <Grid item size={{xs:12, md:6}}>
        <TextField
          fullWidth
          label="Annual Revenue"
          value={formData.revenue}
          onChange={(e) => handleInputChange('revenue', e.target.value)}
          variant="outlined"
          size="small"
          placeholder="$1,000,000"
          error={!!errors.revenue || !!fieldErrors.revenue}
          helperText={errors.revenue || fieldErrors.revenue || ' '}
          sx={formInputStyle}
        />
      </Grid>

      <Grid item size={{xs:12, md:6}}>
        <TextField
          fullWidth
          label="Founded Year"
          value={formData.foundedYear}
          onChange={(e) => handleInputChange('foundedYear', e.target.value)}
          variant="outlined"
          size="small"
          type="number"
          placeholder="2020"
          inputProps={{ min: 1600, max: new Date().getFullYear() }}
          error={!!errors.foundedYear || !!fieldErrors.foundedYear}
          helperText={errors.foundedYear || fieldErrors.foundedYear || ' '}
          sx={formInputStyle}
        />
      </Grid>
      <Grid item size={{xs:12, md:6}}>
        <TextField
          fullWidth
          label="Business Hours"
          value={formData.businessHours}
          onChange={(e) => handleInputChange('businessHours', e.target.value)}
          variant="outlined"
          size="small"
          placeholder="e.g., 9:00 AM - 6:00 PM"
          inputProps={{ maxLength: 100 }}
          error={!!errors.businessHours || !!fieldErrors.businessHours}
          helperText={errors.businessHours || fieldErrors.businessHours || ' '}
          sx={formInputStyle}
        />
      </Grid>

      <Grid item size={{xs:12, md:6}}>
        <TextField
          fullWidth
          label="Time Zone"
          value={formData.timeZone}
          onChange={(e) => handleInputChange('timeZone', e.target.value)}
          variant="outlined"
          size="small"
          placeholder="e.g., EST, PST, GMT"
          inputProps={{ maxLength: 50 }}
          error={!!errors.timeZone || !!fieldErrors.timeZone}
          helperText={errors.timeZone || fieldErrors.timeZone || ' '}
          sx={formInputStyle}
        />
      </Grid>
      <Grid item size={{xs:12, md:6}}>
        <TextField
          fullWidth
          label="Primary Language"
          value={formData.primaryLanguage}
          onChange={(e) => handleInputChange('primaryLanguage', e.target.value)}
          variant="outlined"
          size="small"
          placeholder="e.g., English, Spanish, French"
          inputProps={{ maxLength: 50 }}
          error={!!errors.primaryLanguage || !!fieldErrors.primaryLanguage}
          helperText={errors.primaryLanguage || fieldErrors.primaryLanguage || ' '}
          sx={formInputStyle}
        />
      </Grid>

      <Grid item size={{xs:12, md:6}}>
        <TextField
          fullWidth
          label="Currency"
          value={formData.currency}
          onChange={(e) => handleInputChange('currency', e.target.value.toUpperCase())}
          variant="outlined"
          size="small"
          placeholder="USD"
          inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
          error={!!errors.currency || !!fieldErrors.currency}
          helperText={errors.currency || fieldErrors.currency || '3-letter currency code (e.g., USD)'}
          sx={formInputStyle}
        />
      </Grid>
    </Grid>
  );
};

export default OperationalInformation;
